// Roster service that combines guild API with private API data
import { fetchGuildMembers, categorizeByTeam, convertToRosterFormat } from './guildApi';
import {
  API_CONFIG,
  displayWeeksMetRequirement,
  getApiUrl,
  GUILD_REGION,
  profileRealmMatchesGuildRow,
  normalizeRealmSlug,
} from '../config/apiConfig';

/**
 * Roster identity is guild-members `name` + `realm` + rank (only that row is on the roster; region is US).
 * character-profiles by name can return another realm's toon — never prefer it over guild class/spec; validate realm for scores/avatar.
 */

export interface EnrichedRosterMember {
  id: string;
  name: string;
  level: number;
  rank: string;
  rankNumber: number;
  realm: string;
  region: string;
  team: 'mythic' | 'heroic';
  last_updated: string;
  class: string;
  spec: string;
  character_class: string;
  race: string;
  mythic_plus_score: number;
  profile_url: string;
  mythicPlusThisWeek: number;
  weeksMetRequirement: number;
  avatar?: string | null; // Character avatar URL
}

/**
 * Fetch all weekly mythic counts for the guild in one API call
 */
async function fetchAllWeeklyCounts(): Promise<Map<string, number>> {
  const weeklyCounts = new Map<string, number>();
  
  try {
    const countsUrl = getApiUrl('guild-mythic-counts');
    const countsResponse = await fetch(countsUrl, {
      headers: { 'X-API-Key': API_CONFIG.API_KEY },
      method: 'GET',
      mode: 'cors',
      signal: AbortSignal.timeout(60000), // 60 second timeout
    });

    if (countsResponse.ok) {
      const countsData = await countsResponse.json();
      
      if (countsData.data && Array.isArray(countsData.data)) {
        countsData.data.forEach((player: any) => {
          if (player.Name && player.count !== undefined) {
            const key = player.Name.toLowerCase();
            weeklyCounts.set(key, Math.min(player.count, 8)); // Cap at 8
          }
        });
      }
    }
  } catch (countsError) {
    console.error(`Weekly counts fetch failed:`, countsError);
  }
  
  return weeklyCounts;
}

/**
 * Fetch character data from private API. Returns null if the profile is for another realm (same name) or on failure.
 */
async function fetchPrivateAPIData(name: string, realm: string, _region: string = GUILD_REGION): Promise<{
  class: string;
  spec: string;
  race: string;
  mythic_plus_score: number;
  profile_url: string;
  avatar?: string | null;
} | null> {
  try {
    const profileUrl = getApiUrl(
      `character-profiles/${encodeURIComponent(name)}?realm=${encodeURIComponent(realm)}&region=${encodeURIComponent(GUILD_REGION)}`
    );
    const profileResponse = await fetch(profileUrl, {
      headers: { 'X-API-Key': API_CONFIG.API_KEY },
      mode: 'cors',
      signal: AbortSignal.timeout(60000), // 60 second timeout
    });

    if (!profileResponse.ok) {
      return null;
    }

    const data = await profileResponse.json();
    if (!data?.data) {
      return null;
    }

    const profile = data.data;
    if (!profileRealmMatchesGuildRow(profile.realm, realm)) {
      return null;
    }

    const classData = profile.character_class || 'Unknown';
    const specData =
      profile.spec != null && profile.spec !== ''
        ? profile.spec
        : profile.character_class || classData || 'Unknown';
    const raceData = profile.race || 'Unknown';
    const mythicScore = profile.MythicScore || 0;
    const avatarData = profile.avatar_url || profile.avatar || profile.character_image || null;

    return {
      class: classData,
      spec: specData,
      race: raceData,
      mythic_plus_score: mythicScore,
      profile_url: profileUrl,
      avatar: avatarData,
    };
  } catch (error) {
    console.error(`Error fetching private API data for ${name}:`, error);
    return null;
  }
}

/**
 * Realm-scoped portrait when guild API profile is missing or rejected (name collision on character-profiles).
 * Raider.io matches region + realm + name and returns Blizzard render URLs.
 */
async function fetchRaiderIoThumbnail(
  name: string,
  realm: string,
  region: string = GUILD_REGION
): Promise<string | null> {
  if (!name?.trim() || !realm?.trim()) {
    return null;
  }
  try {
    const realmSlug = normalizeRealmSlug(realm);
    const url = `https://raider.io/api/v1/characters/profile?region=${encodeURIComponent(region)}&realm=${encodeURIComponent(realmSlug)}&name=${encodeURIComponent(name.trim())}&fields=thumbnail_url`;
    const res = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      signal: AbortSignal.timeout(20000),
    });
    if (!res.ok) {
      return null;
    }
    const json = await res.json();
    const thumb = json.thumbnail_url;
    return typeof thumb === 'string' && thumb.length > 0 ? thumb : null;
  } catch {
    return null;
  }
}

/**
 * Enrich guild member data with private API information and Mythic+ tracking data
 */
async function enrichMemberWithPrivateAPI(
  member: any, 
  mythicPlusData: Map<string, { mythicPlusThisWeek: number; weeksMetRequirement: number }>,
  weeklyCounts: Map<string, number>
): Promise<EnrichedRosterMember> {
  // In development, you can skip private API calls by setting this to true
  const SKIP_PRIVATE_API_IN_DEV = false;
  const isDevelopment = process.env.NODE_ENV === 'development';
  
  let privateAPIData = null;
  
  if (!isDevelopment || !SKIP_PRIVATE_API_IN_DEV) {
    privateAPIData = await fetchPrivateAPIData(member.name, member.realm, GUILD_REGION);
  }

  let avatar =
    privateAPIData?.avatar ??
    (await fetchRaiderIoThumbnail(member.name, member.realm, GUILD_REGION));

  // Get weekly count from the pre-fetched weekly counts map
  const weeklyCount = weeklyCounts.get(member.name.toLowerCase()) ?? 0;
  
  // Get Firebase data as fallback for weekly counts
  const key = `${member.name}|${member.realm || ''}|${GUILD_REGION}`;
  const mythicPlusInfo = mythicPlusData.get(key) || { mythicPlusThisWeek: 0, weeksMetRequirement: 0 };
  
  // Also try looking up by just the lowercase name as fallback (how Firebase function stores data)
  const simpleKey = member.name.toLowerCase();
  const simpleKeyInfo = mythicPlusData.get(simpleKey);
  
  // Also try the hardcoded composite key we're using for storage
  const hardcodedKey = `${member.name}|stormrage|${GUILD_REGION}`;
  const hardcodedInfo = mythicPlusData.get(hardcodedKey);
  
  
  // Use the data from any key, preferring the one with higher weeksMetRequirement
  let finalMythicPlusInfo = mythicPlusInfo;
  if (simpleKeyInfo && simpleKeyInfo.weeksMetRequirement > finalMythicPlusInfo.weeksMetRequirement) {
    finalMythicPlusInfo = simpleKeyInfo;
  }
  if (hardcodedInfo && hardcodedInfo.weeksMetRequirement > finalMythicPlusInfo.weeksMetRequirement) {
    finalMythicPlusInfo = hardcodedInfo;
  }
  
  
  // Use API weekly count if it's a valid number (including 0), otherwise fall back to Firebase data
  const finalWeeklyCount = (weeklyCount !== undefined && weeklyCount !== null) ? weeklyCount : finalMythicPlusInfo.mythicPlusThisWeek;
  
  
  const guildSpec =
    typeof member.spec === 'string' && member.spec.trim() !== '' ? member.spec : '';

  return {
    ...member,
    // Guild row (rank + realm) is authoritative; private API can duplicate names on other realms.
    class: member.character_class || 'Unknown',
    spec: guildSpec || privateAPIData?.spec || member.character_class || 'Unknown',
    character_class: member.character_class || 'Unknown',
    race: privateAPIData?.race || member.race || 'Unknown',
    mythic_plus_score: privateAPIData?.mythic_plus_score || 0,
    profile_url:
      privateAPIData?.profile_url ||
      getApiUrl(
        `character-profiles/${encodeURIComponent(member.name)}?realm=${encodeURIComponent(member.realm || '')}&region=${encodeURIComponent(GUILD_REGION)}`
      ),
    avatar,
    mythicPlusThisWeek: finalWeeklyCount, // Use API data if available, otherwise Firebase fallback
    weeksMetRequirement: finalMythicPlusInfo.weeksMetRequirement,
  };
}

/**
 * Fetch Mythic+ tracking data from new API endpoint with weeks_with_4_plus_runs field
 */
async function fetchMythicPlusData(_members: any[]): Promise<Map<string, { mythicPlusThisWeek: number; weeksMetRequirement: number }>> {
  const mythicPlusData = new Map();
  
  try {
    // Use the new API endpoint with weeks_with_4_plus_runs field
    const countsUrl = getApiUrl('guild-mythic-counts');
    
    const response = await fetch(countsUrl, {
      headers: { 'X-API-Key': API_CONFIG.API_KEY },
      method: 'GET',
      mode: 'cors',
      signal: AbortSignal.timeout(60000), // 60 second timeout
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.data && Array.isArray(data.data)) {
        
        // First pass: collect all players and handle duplicates
        const playerData = new Map();
        
        data.data.forEach((player: any) => {
          if (player.Name && player.weeks_with_4_plus_runs !== undefined) {
            const playerName = player.Name.toLowerCase();
            const weeksMet = displayWeeksMetRequirement(player.weeks_with_4_plus_runs || 0);
            const weeklyCount = player.count || 0;
            
            // Store player data, preferring higher values for duplicates
            if (!playerData.has(playerName) || weeksMet > playerData.get(playerName).weeksMetRequirement) {
              playerData.set(playerName, {
                name: player.Name,
                weeksMetRequirement: weeksMet,
                mythicPlusThisWeek: weeklyCount
              });
            }
          }
        });
        
        // Second pass: store the best data for each player
        playerData.forEach((data, playerName) => {
          // Store with simple lowercase name key
          mythicPlusData.set(playerName, {
            mythicPlusThisWeek: data.mythicPlusThisWeek,
            weeksMetRequirement: data.weeksMetRequirement
          });
          
          // Store with composite keys for different realm variations
          const realmVariations = ['stormrage', 'area-52', 'area52', 'area_52', 'illidan'];
          realmVariations.forEach(realm => {
            const compositeKey = `${data.name}|${realm}|us`;
            mythicPlusData.set(compositeKey, {
              mythicPlusThisWeek: data.mythicPlusThisWeek,
              weeksMetRequirement: data.weeksMetRequirement
            });
          });
        });
        
      }
    } else {
      console.error(`Failed to fetch from new API: ${response.status} ${response.statusText}`);
    }
    
    
  } catch (error) {
    console.error('Failed to fetch Mythic+ data from new API:', error);
  }
  
  return mythicPlusData;
}

/**
 * Fetch complete roster data with class/spec information from private API
 */
export async function fetchEnrichedRoster(): Promise<{
  mythic: EnrichedRosterMember[];
  heroic: EnrichedRosterMember[];
  loading: boolean;
  error: string | null;
}> {
  try {
    // Fetch guild members from API
    const guildMembers = await fetchGuildMembers();
    
    // Categorize by team
    const { mythic, heroic } = categorizeByTeam(guildMembers);
    const mythicRoster = mythic.map(convertToRosterFormat);
    const heroicRoster = heroic.map(convertToRosterFormat);
    
    // Fetch Mythic+ tracking data from Firebase
    const mythicPlusData = await fetchMythicPlusData([...mythicRoster, ...heroicRoster]);
    
    
    // Fetch all weekly counts in one API call
    const weeklyCounts = await fetchAllWeeklyCounts();
    
    // Enrich with private API data (no batching needed - API doesn't rate limit)
    
    // Use Promise.allSettled to handle individual failures gracefully
    const [mythicResults, heroicResults] = await Promise.all([
      Promise.allSettled(mythicRoster.map(member => enrichMemberWithPrivateAPI(member, mythicPlusData, weeklyCounts))),
      Promise.allSettled(heroicRoster.map(member => enrichMemberWithPrivateAPI(member, mythicPlusData, weeklyCounts)))
    ]);
    
    // Extract successful results and log failures
    const enrichedMythic = mythicResults
      .map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          console.warn(`Failed to enrich mythic member ${mythicRoster[index]?.name}:`, result.reason);
          return mythicRoster[index]; // Return unenriched member as fallback
        }
      });
    
    const enrichedHeroic = heroicResults
      .map((result, index) => {
        if (result.status === 'fulfilled') {
          return result.value;
        } else {
          console.warn(`Failed to enrich heroic member ${heroicRoster[index]?.name}:`, result.reason);
          return heroicRoster[index]; // Return unenriched member as fallback
        }
      });
    
    return {
      mythic: enrichedMythic,
      heroic: enrichedHeroic,
      loading: false,
      error: null,
    };
    
  } catch (error) {
    console.error('Failed to fetch enriched roster:', error);
    
    // Return error state instead of mock data
    return {
      mythic: [],
      heroic: [],
      loading: false,
      error: error instanceof Error ? error.message : 'Failed to fetch roster data'
    };
  }
}

/**
 * Search for a specific member across both teams
 */
export async function searchRosterMember(characterName: string): Promise<EnrichedRosterMember | null> {
  try {
    const guildMembers = await fetchGuildMembers();
    const member = guildMembers.find(m => m.name.toLowerCase() === characterName.toLowerCase());
    
    if (!member) {
      return null;
    }
    
    const rosterMember = convertToRosterFormat(member);
    // For search, we don't have mythicPlusData, so we'll pass an empty map or a mock
    // This might need adjustment depending on how searchRosterMember is used
    // For now, we'll pass an empty map as a placeholder
    const mythicPlusData = new Map();
    const weeklyCounts = new Map();
    return await enrichMemberWithPrivateAPI(rosterMember, mythicPlusData, weeklyCounts);
  } catch (error) {
    console.error('Failed to search roster member:', error);
    return null;
  }
}
